import { z } from "zod";
import { IdType, sortQuery } from "./base.validator";
import { fileMeta } from "./files.validator";
import { paginateSchema, paginationMeta } from "./pagination.validator";
import { createdDepartment } from "./created-department";
import { createdTimeAway } from "./leave.validator";

// profiles.validator.ts
export enum MaritalStatus {
	Single = "Single",
	Married = "Married",
	Separated = "Separated",
	Divorced = "Divorced",
	Widowed = "Widowed",
}

export enum Gender {
	Male = "Male",
	Female = "Female",
}

export type CreateStaffInput = z.infer<typeof createStaffInput>;

export const createdProfile = z.object({
	id: IdType,
	firstName: z.string({ required_error: "This field is required" }),
	lastName: z.string({ required_error: "This field is required" }),
	birthDate: z.string().datetime().nullable(),
	maritalStatus: z.nativeEnum(MaritalStatus).nullable(),
	gender: z.nativeEnum(Gender).nullable(),

	addressStreet1: z.string().nullable(),
	addressStreet2: z.string().nullable(),
	city: z.string().nullable(),
	province: z.string().nullable(),
	postalCode: z.string().nullable(),
	country: z.string().nullable(),

	// Contact Properties
	workPhone: z.string().nullable(),
	mobilePhone: z.string().nullable(),
	homePhone: z.string().nullable(),
	workEmail: z.string({ required_error: "This field is required" }).email(),
	personalEmail: z.string().email().nullable(),

	// Emergency Contact Properties
	emergencyName: z.string().nullable(),
	emergencyRelationship: z.string().nullable(),
	emergencyMobilePhone: z.string().nullable(),

	// Work Properties
	jobTitle: z.string({ required_error: "This field is required" }).nullable(),
	employmentStatus: z
		.string({ required_error: "This field is required" })
		.nullable(),

	// Profile Picure
	profilePicture: fileMeta.nullish(),
	contract: fileMeta.nullish(),

	createdAt: z.string({ required_error: "This field is required" }).datetime(),
	department: createdDepartment.nullable(),

	activatedAt: z.string().datetime().nullable(),
});

export type CreateProfileDtos = z.infer<typeof createdProfile>;

// base profile
export const createStaffInput = z.object({
	profile: createdProfile
		.omit({
			id: true,
			department: true,
			profilePicture: true,
			// contract: true,
			activatedAt: true,
		})
		.extend({
			departmentId: IdType,
			profilePictureId: IdType.nullable(),
			contractId: z.string({ required_error: "This field is required" }),
		})
		.partial()
		.required({
			firstName: true,
			lastName: true,
			jobTitle: true,
			employmentStatus: true,
			workEmail: true,
			createdAt: true,
		}),
	leaveCredits: createdTimeAway
		.pick({
			hours: true,
			timeAwayTypeId: true,
		})
		.array(),
});

export const updateProfileInput = createStaffInput
	.omit({ leaveCredits: true })
	.extend({
		profile: createStaffInput.shape.profile
			.omit({ createdAt: true })
			.partial()
			.required({
				firstName: true,
				lastName: true,
				birthDate: true,
			}),
	});

export type UpdateProfileInput = z.infer<typeof updateProfileInput>;
export const updateProfileData = updateProfileInput.extend({
	profile: updateProfileInput.shape.profile.extend({
		departmentName: z.string().optional(),
		departmentHead: z.string().optional(),
	}),
});
export type UpdateProfileData = z.infer<typeof updateProfileData>;

export const updateProfile = createdProfile
	.omit({ id: true, department: true })
	.extend({ departmentId: IdType })
	.partial();

export const findProfileSort = createdProfile
	.pick({ lastName: true, firstName: true, createdAt: true })
	.extend({
		lastName: sortQuery,
		firstName: sortQuery,
		createdAt: sortQuery,
	})
	.partial();

export const paginatedCreatedProfile = paginateSchema(createdProfile);

export type PaginatedCreatedProfile = z.infer<typeof paginatedCreatedProfile>;

export const findProfileFilter = z
	.object({
		id: IdType,
		accountId: IdType,
		nameOrEmail: z.string(),
		isActivated: z.enum(["true", "false"]).transform((v) => {
			switch (v) {
				case "true":
					return true;
				case "false":
					return false;
			}
		}),
	})
	.partial();

export const findProfileQuery = z
	.object({
		sort: findProfileSort,
	})
	.merge(findProfileFilter)
	.merge(paginationMeta)
	.partial();

export type FindProfileQuery = z.infer<typeof findProfileQuery>;

// export const resendInviteId = z.void();
